import { PieChart, Pie } from 'recharts';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Info, AlertTriangle } from 'lucide-react';
import type { PerformanceSnapshotApi } from '@/types/api/performance-snapshot';

/** Stable keys for the loading skeleton placeholders */
const SKELETON_KEYS = ['customers', 'dso', 'promise-success', 'avg-payment-days'] as const;

type PerformanceSnapshotContentProps = Readonly<{
  isLoading: boolean;
  isError: boolean;
  performanceSnapshot: PerformanceSnapshotApi | undefined;
  /** Fallback values used when performanceSnapshot is undefined */
  fallback: {
    customerCount: number;
    dsoCurrent: number;
    dsoTarget: number;
    promiseSuccessRate: number;
    avgPaymentDays: number;
    collectionRate: number;
    collectionRateTarget: number;
  };
  formatCurrency: (value: number) => string;
}>

function PerformanceSnapshotSkeleton() {
  return (
    <div className="space-y-6">
      <div className="pb-3 border-b border-border">
        <div className="h-3 w-20 bg-gray-200 rounded animate-pulse mb-3" />
        <div className="grid grid-cols-4 gap-5">
          {SKELETON_KEYS.map((key) => (
            <div key={key} className="space-y-2 animate-pulse">
              <div className="h-4 w-24 bg-gray-200 rounded" />
              <div className="h-8 w-16 bg-gray-200 rounded" />
              <div className="h-3 w-28 bg-gray-200 rounded" />
            </div>
          ))}
        </div>
      </div>
      <div className="flex flex-col items-center animate-pulse">
        <div className="h-3 w-24 bg-gray-200 rounded mb-4" />
        <div className="w-[200px] h-[200px] rounded-full bg-gray-200" />
      </div>
    </div>
  );
}

/**
 * Renders the inner content of the Performance Snapshot card.
 * Extracted from UnifiedCreditControlDashboard to reduce cognitive complexity
 * and avoid nested ternaries.
 */
export function PerformanceSnapshotContent({
  isLoading,
  isError,
  performanceSnapshot,
  fallback,
  formatCurrency,
}: PerformanceSnapshotContentProps) {
  if (isLoading) {
    return <PerformanceSnapshotSkeleton />;
  }

  if (isError) {
    return (
      <div className="text-sm text-destructive">
        Unable to load performance snapshot data
      </div>
    );
  }

  const customerCount = performanceSnapshot?.customerCount ?? fallback.customerCount;
  const dso = performanceSnapshot?.dso ?? fallback.dsoCurrent;
  const dsoTarget = performanceSnapshot?.dsoTarget ?? fallback.dsoTarget;
  const promiseSuccessPercentage = performanceSnapshot?.promiseSuccessPercentage ?? fallback.promiseSuccessRate;
  const promisesKept = performanceSnapshot?.promisesKept ?? 21;
  const promisesTotal = performanceSnapshot?.promisesTotal ?? 40;
  const avgPaymentDays = performanceSnapshot?.avgPaymentDays ?? fallback.avgPaymentDays;
  const collectionRate = performanceSnapshot?.collectionRate ?? fallback.collectionRate;
  const collectionRateTarget = performanceSnapshot?.collectionRateTarget ?? fallback.collectionRateTarget;
  const collectedAmount = performanceSnapshot?.collectedAmount ?? 1800000;
  const totalDueAmount = performanceSnapshot?.totalDueAmount ?? 4200000;
  const targetGap = collectionRateTarget - collectionRate;

  return (
    <>
      {/* Section 1: Key Metrics */}
      <div className="pb-3 border-b border-border">
        <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground mb-3">
          Key Metrics
        </p>
        <div className="grid grid-cols-4 gap-5">
          <div>
            <p className="text-base text-foreground mb-2">Customers</p>
            <p className="text-2xl font-bold text-foreground leading-none mb-3">{customerCount}</p>
            <p className="text-sm text-muted-foreground">Total customers</p>
          </div>
          <div>
            <p className="text-base text-foreground mb-2">DSO</p>
            <p className="text-2xl font-bold text-foreground leading-none mb-3">{dso}</p>
            <p className="text-sm text-muted-foreground">Target: {dsoTarget} days</p>
          </div>
          <div>
            <p className="text-base text-foreground mb-2">Promise Success</p>
            <p className="text-2xl font-bold text-foreground leading-none mb-3">{promiseSuccessPercentage}%</p>
            <p className="text-sm text-muted-foreground">{promisesKept} of {promisesTotal} kept</p>
          </div>
          <div>
            <p className="text-base text-foreground mb-2">Avg Payment Days</p>
            <p className="text-2xl font-bold text-foreground leading-none mb-3">{avgPaymentDays}</p>
            <p className="text-sm text-muted-foreground">Target: {dsoTarget} days</p>
          </div>
        </div>
      </div>

      {/* Section 2: Collection Rate - Donut Chart */}
      <div className="flex flex-col items-center">
        <div className="flex items-center gap-2 mb-4">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            Collection Rate
          </p>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-4 w-4 text-muted-foreground cursor-help" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <div className="space-y-2">
                  <p className="font-semibold">Collection Rate Calculation:</p>
                  <p className="text-sm">Cash collected this month &divide; Total cash due this month</p>
                  <p className="text-sm">{formatCurrency(collectedAmount)} &divide; {formatCurrency(totalDueAmount)} = {collectionRate}%</p>
                  <p className="text-sm font-semibold mt-2">Target: {collectionRateTarget}% (industry benchmark)</p>
                  <p className="text-sm">Current gap: {targetGap} percentage points below target</p>
                </div>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
        
        {/* Donut Chart */}
        <div className="relative w-[200px] h-[200px] my-3 flex items-center justify-center">
          <PieChart width={200} height={200}>
            <Pie
              data={[
                { value: collectionRate, fill: 'oklch(0.5 0.09 194.77)' },
                { value: 100 - collectionRate, fill: 'oklch(0.9499 0.0059 239.82)' }
              ]}
              cx={100}
              cy={100}
              innerRadius={72}
              outerRadius={90}
              startAngle={90}
              endAngle={450}
              dataKey="value"
              stroke="none"
            />
          </PieChart>
          
          {/* Center Text */}
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <p className="text-4xl font-bold text-foreground leading-none">
              {collectionRate}%
            </p>
          </div>
        </div>
        
        <p className="text-[13px] text-muted-foreground text-center flex items-center gap-1">
          Target: {collectionRateTarget}% &bull; <span className="text-warning-600 font-medium">{targetGap} points below target</span>
          <AlertTriangle className="h-3 w-3 text-warning-500" />
        </p>
      </div>
    </>
  );
}
